import {encode} from 'blurhash'

// Small size for fast encoding
const BLURHASH_WIDTH = 32
const BLURHASH_HEIGHT = 32

/**
 * Generate a blurhash string from an image URI.
 *
 * Web implementation using canvas to extract pixel data.
 * Uses 4 components on the longer dimension, 3 on the shorter (matching Element-web).
 */
export async function generateBlurhash(
  uri: string,
  width: number,
  height: number,
): Promise<string | null> {
  try {
    const img = await loadImage(uri)

    // Resize to a small version for fast encoding
    const aspect = width / height
    const encodeW =
      aspect >= 1 ? BLURHASH_WIDTH : Math.round(BLURHASH_HEIGHT * aspect)
    const encodeH =
      aspect >= 1 ? Math.round(BLURHASH_WIDTH / aspect) : BLURHASH_HEIGHT

    const canvas = document.createElement('canvas')
    canvas.width = encodeW
    canvas.height = encodeH
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.drawImage(img, 0, 0, encodeW, encodeH)
    const imageData = ctx.getImageData(0, 0, encodeW, encodeH)

    // 4 components on longer dimension, 3 on shorter
    const componentX = aspect >= 1 ? 4 : 3
    const componentY = aspect >= 1 ? 3 : 4

    return encode(imageData.data, encodeW, encodeH, componentX, componentY)
  } catch {
    return null
  }
}

function loadImage(uri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load image'))
    img.crossOrigin = 'anonymous'
    img.src = uri
  })
}
